import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Loader2, CalendarClock, Check, Clock, Pencil, Trash2, Truck, Repeat, DollarSign, Mail, Search, FileText } from 'lucide-react';
import {
  ALERT_CATEGORIES, ALERT_PRIORITIES, ALERT_RECURRENCES, prioMeta, fmtDate,
  urgencyBadge, categoryLabel, recurrenceLabel, SOURCE_META,
} from './alertsShared';
import WeeklySummarySettings from './WeeklySummarySettings';

const emptyForm = {
  title: '', description: '', category: 'seguro', custom_category: '',
  alert_date: '', alert_time: '', priority: 'media', notify_days_before: 3,
  recurrence: 'none', recur_interval: 3, truck_id: '', truck_label: '',
};

export default function AlertsManager({ API }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [myMap, setMyMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pendente');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [trucks, setTrucks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snoozeOpenId, setSnoozeOpenId] = useState(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uni, mine] = await Promise.all([
        axios.get(`${API}/admin/calendar-items`, auth()),
        axios.get(`${API}/admin/alerts`, auth()),
      ]);
      setItems(uni.data.items || []);
      setCounts(uni.data.counts || {});
      const map = {};
      (mine.data.alerts || []).forEach((a) => { map[a.id] = a; });
      setMyMap(map);
    } catch (e) {
      console.error('Erro ao carregar calendário', e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const loadTrucks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trucks`, auth());
      setTrucks(Array.isArray(data) ? data : (data.trucks || []));
    } catch (e) { /* ignore */ }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTrucks(); }, [loadTrucks]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (id) => {
    const a = myMap[id];
    if (!a) return;
    setEditingId(id);
    setForm({
      title: a.title || '', description: a.description || '',
      category: a.category || 'outro', custom_category: a.custom_category || '',
      alert_date: a.alert_date || '', alert_time: a.alert_time || '',
      priority: a.priority || 'media', notify_days_before: a.notify_days_before ?? 3,
      recurrence: a.recurrence || 'none', recur_interval: a.recur_interval ?? 3,
      truck_id: a.truck_id || '', truck_label: a.truck_label || '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Informe o título'); return; }
    if (!form.alert_date) { toast.error('Informe a data'); return; }
    setSaving(true);
    try {
      const payload = { ...form, notify_days_before: Number(form.notify_days_before) || 0, recur_interval: Number(form.recur_interval) || 1 };
      if (editingId) {
        await axios.put(`${API}/admin/alerts/${editingId}`, payload, auth());
        toast.success('Alerta atualizado');
      } else {
        await axios.post(`${API}/admin/alerts`, payload, auth());
        toast.success('Alerta criado');
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error('Erro ao salvar alerta');
    } finally {
      setSaving(false);
    }
  };

  const resolveAlert = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/alerts/${id}/resolve`, {}, auth());
      toast.success(data.recurred ? `Concluído — próxima em ${fmtDate(data.next_date)}` : 'Alerta resolvido ✅');
      await load();
    } catch { toast.error('Erro ao resolver'); }
  };

  const snoozeAlert = async (id, days) => {
    try {
      await axios.post(`${API}/admin/alerts/${id}/snooze`, { days }, auth());
      toast.success(`Adiado por ${days} dia(s)`);
      await load();
    } catch { toast.error('Erro ao adiar'); }
  };

  const removeAlert = async (id) => {
    if (!window.confirm('Excluir este alerta permanentemente?')) return;
    try {
      await axios.delete(`${API}/admin/alerts/${id}`, auth());
      toast.success('Alerta excluído');
      await load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const markBillPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/company-bills/${id}/mark-paid`, {}, auth());
      toast.success('Conta marcada como paga ✅');
      await load();
    } catch { toast.error('Erro ao marcar como paga'); }
  };

  const onTruckChange = (truckId) => {
    if (!truckId) { setForm((f) => ({ ...f, truck_id: '', truck_label: '' })); return; }
    const t = trucks.find((x) => x.id === truckId);
    const label = t ? `${t.placa || ''}${t.modelo ? ' - ' + t.modelo : ''}`.trim() : '';
    setForm((f) => ({ ...f, truck_id: truckId, truck_label: label }));
  };

  const openInsuranceDoc = async (insuranceId) => {
    try {
      const res = await axios.get(`${API}/admin/insurances/${insuranceId}/primary-document`, { responseType: 'blob', ...auth() });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'Este seguro não tem documento anexado' : 'Erro ao abrir documento');
    }
  };

  const visibleItems = items.filter((it) => {
    if (statusFilter === 'resolvido' && it.status !== 'resolvido') return false;
    if (statusFilter === 'pendente' && it.status === 'resolvido') return false;
    if (sourceFilter !== 'all' && it.source !== sourceFilter) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${it.title || ''} ${it.obs || ''} ${it.category || ''} ${it.truck_label || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const PRIO_RANK = { urgente: 4, alta: 3, media: 2, baixa: 1 };
  const sortedItems = [...visibleItems].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'date') cmp = (a.date || '').localeCompare(b.date || '');
    else if (sortBy === 'priority') cmp = (PRIO_RANK[a.priority] || 0) - (PRIO_RANK[b.priority] || 0);
    else if (sortBy === 'value') cmp = (Number(a.value) || 0) - (Number(b.value) || 0);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'date' ? 'asc' : 'desc');
    }
  };
  const sortArrow = (key) => (sortBy === key ? (sortDir === 'asc' ? '↑' : '↓') : '');

  return (
    <div className="space-y-4" data-testid="alerts-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-indigo-600" /> Calendário de Alertas
          </h3>
          <p className="text-xs text-slate-500">Sincronizado 100% com Contas a Pagar: alertas + contas + validades num só lugar.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setWeeklyOpen(true)} data-testid="weekly-summary-btn" className="text-xs">
            <Mail className="w-4 h-4 mr-1" /> Resumo Semanal
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="alert-create-btn" className="text-xs">
            <Plus className="w-4 h-4 mr-1" /> Novo Alerta
          </Button>
        </div>
      </div>

      <WeeklySummarySettings API={API} open={weeklyOpen} onClose={() => setWeeklyOpen(false)} />

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="alerts-summary">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="text-2xl font-bold text-red-600">{counts.overdue || 0}</div>
          <div className="text-xs text-red-700">Vencidos</div>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
          <div className="text-2xl font-bold text-orange-600">{counts.today || 0}</div>
          <div className="text-xs text-orange-700">Hoje</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-2xl font-bold text-amber-600">{counts.soon || 0}</div>
          <div className="text-xs text-amber-700">Em breve</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-2xl font-bold text-emerald-600">{counts.resolved || 0}</div>
          <div className="text-xs text-emerald-700">Resolvidos</div>
        </div>
      </div>

      {/* Filtro status */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
          {[['pendente', 'Pendentes'], ['resolvido', 'Resolvidos'], ['all', 'Todos']].map(([v, l]) => (
            <button
              key={v}
              data-testid={`alerts-filter-${v}`}
              onClick={() => setStatusFilter(v)}
              className={`text-xs px-3 py-1 rounded-md transition-colors ${statusFilter === v ? 'bg-white shadow text-indigo-700 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
            >{l}</button>
          ))}
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
          {[['all', 'Todas origens'], ['alert', 'Alertas'], ['bill', 'Contas'], ['company_alert', 'Validades']].map(([v, l]) => (
            <button
              key={v}
              data-testid={`alerts-source-${v}`}
              onClick={() => setSourceFilter(v)}
              className={`text-xs px-3 py-1 rounded-md transition-colors ${sourceFilter === v ? 'bg-white shadow text-indigo-700 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
            >{l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="alerts-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, obs, categoria, caminhão…"
            className="pl-8 h-8 text-xs"
          />
          {search && (
            <button data-testid="alerts-search-clear" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm">✕</button>
          )}
        </div>
      </div>

      {/* Ordenação */}
      <div className="flex items-center gap-2 text-xs" data-testid="alerts-sort">
        <span className="text-slate-400">Ordenar:</span>
        {[['date', 'Data'], ['priority', 'Prioridade'], ['value', 'Valor']].map(([k, l]) => (
          <button
            key={k}
            data-testid={`alerts-sort-${k}`}
            onClick={() => toggleSort(k)}
            className={`px-2.5 py-1 rounded-md border transition-colors ${sortBy === k ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
          >{l} {sortArrow(k)}</button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…</div>
      ) : sortedItems.length === 0 ? (
        <div className="text-center text-slate-400 py-10 text-sm" data-testid="alerts-empty">Nenhum item neste filtro.</div>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          {sortedItems.map((it) => {
            const ub = urgencyBadge(it);
            const src = SOURCE_META[it.source] || SOURCE_META.alert;
            const resolved = it.status === 'resolvido';
            const isAlert = it.source === 'alert';
            const isBill = it.source === 'bill';
            const rec = isAlert ? recurrenceLabel(it.recurrence, it.recur_interval ?? myMap[it.id]?.recur_interval) : null;
            const pm = it.priority ? prioMeta(it.priority) : null;
            const insId = (isAlert && myMap[it.id]?.source === 'insurance') ? (myMap[it.id]?.source_key || '').split(':')[1] : null;
            return (
              <div key={`${it.source}-${it.id}`} data-testid={`alert-row-${it.id}`}
                className={`rounded-xl border p-3 ${resolved ? 'bg-slate-50 opacity-75' : 'bg-white'} shadow-sm`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${src.cls}`}>{src.label}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ub.cls}`}>{ub.text}</span>
                      {pm && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pm.cls}`}>{pm.label}</span>}
                      <span className="text-xs text-slate-500">{isAlert ? categoryLabel(it) : (it.category || '')}</span>
                      {rec && <span className="text-[10px] text-indigo-600 inline-flex items-center gap-0.5"><Repeat className="w-3 h-3" />{rec}</span>}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-800">{it.title}</div>
                    {it.obs && <div className="text-xs text-slate-500 mt-0.5 italic">📝 {it.obs}</div>}
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span>📅 {fmtDate(it.date)}</span>
                      {isBill && it.value != null && <span className="inline-flex items-center gap-0.5 text-orange-600 font-semibold"><DollarSign className="w-3 h-3" />{Number(it.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}
                      {it.truck_label && <span className="inline-flex items-center gap-1"><Truck className="w-3 h-3" /> {it.truck_label}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 flex-shrink-0">
                    {isAlert && !resolved && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-emerald-700" data-testid={`alert-resolve-${it.id}`} onClick={() => resolveAlert(it.id)}>
                          <Check className="w-3 h-3 mr-1" /> {rec ? 'Concluir' : 'Resolver'}
                        </Button>
                        <div className="relative inline-block">
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2" data-testid={`alert-snooze-${it.id}`}
                            onClick={() => setSnoozeOpenId(snoozeOpenId === it.id ? null : it.id)}>
                            <Clock className="w-3 h-3 mr-1" /> Adiar
                          </Button>
                          {snoozeOpenId === it.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setSnoozeOpenId(null)} />
                              <div className="absolute right-0 z-20 bg-white border rounded-lg shadow-lg p-1 mt-0.5 w-24">
                                {[[1, '1 dia'], [3, '3 dias'], [7, '1 semana']].map(([d, l]) => (
                                  <button key={d} data-testid={`alert-snooze-${it.id}-${d}`} onClick={() => { setSnoozeOpenId(null); snoozeAlert(it.id, d); }}
                                    className="block w-full text-left text-xs px-2 py-1 rounded hover:bg-slate-100">{l}</button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2" data-testid={`alert-edit-${it.id}`} onClick={() => openEdit(it.id)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-red-600" data-testid={`alert-delete-${it.id}`} onClick={() => removeAlert(it.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    {isBill && (
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-emerald-700" data-testid={`bill-paid-${it.id}`} onClick={() => markBillPaid(it.id)}>
                        <Check className="w-3 h-3 mr-1" /> Marcar pago
                      </Button>
                    )}
                    {insId && (
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-indigo-700" data-testid={`alert-insurance-doc-${it.id}`} onClick={() => openInsuranceDoc(insId)}>
                        <FileText className="w-3 h-3 mr-1" /> Documento
                      </Button>
                    )}
                    {it.source === 'company_alert' && (
                      <span className="text-[10px] text-slate-400 self-center">gerencie em Contas a Pagar</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal criar/editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="alert-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Alerta' : 'Novo Alerta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título *</Label>
              <Input data-testid="alert-input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Seguro FFD1002 vence" />
            </div>
            <div>
              <Label className="text-xs">Descrição</Label>
              <Textarea data-testid="alert-input-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Detalhes do alerta…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Categoria</Label>
                <select data-testid="alert-input-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  {ALERT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Prioridade</Label>
                <select data-testid="alert-input-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  {ALERT_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            {form.category === 'outro' && (
              <div>
                <Label className="text-xs">Categoria personalizada</Label>
                <Input data-testid="alert-input-custom-category" value={form.custom_category} onChange={(e) => setForm({ ...form, custom_category: e.target.value })} placeholder="Ex: Reunião, Follow-up…" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Data *</Label>
                <Input type="date" data-testid="alert-input-date" value={form.alert_date} onChange={(e) => setForm({ ...form, alert_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Hora</Label>
                <Input type="time" data-testid="alert-input-time" value={form.alert_time} onChange={(e) => setForm({ ...form, alert_time: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Avisar antes (dias)</Label>
                <Input type="number" min={0} data-testid="alert-input-notify-before" value={form.notify_days_before} onChange={(e) => setForm({ ...form, notify_days_before: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><Repeat className="w-3 h-3" /> Recorrência</Label>
                <select data-testid="alert-input-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  {ALERT_RECURRENCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {form.recurrence === 'custom' && (
                <div>
                  <Label className="text-xs">A cada quantos meses</Label>
                  <Input type="number" min={1} data-testid="alert-input-recur-interval" value={form.recur_interval} onChange={(e) => setForm({ ...form, recur_interval: e.target.value })} />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Vincular a caminhão (opcional)</Label>
              <select data-testid="alert-input-truck" value={form.truck_id} onChange={(e) => onTruckChange(e.target.value)}
                className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                <option value="">— Nenhum —</option>
                {trucks.map((t) => <option key={t.id} value={t.id}>{t.placa}{t.modelo ? ` - ${t.modelo}` : ''}</option>)}
              </select>
            </div>
            {form.recurrence !== 'none' && (
              <p className="text-[11px] text-indigo-600 bg-indigo-50 rounded-md p-2">🔁 Ao resolver, este alerta será recriado automaticamente para a próxima data.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="alert-cancel-btn">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="alert-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Salvar' : 'Criar Alerta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
